// KidMealsManager — adult-facing sheet listing every recipe (bundled
// + the adult's own user_recipes) with a checkbox to mark each as
// "available for the kids to request". Toggles a row in
// kid_recipes for this adult; the kid's Foods → Meals chip reads
// across the whole family graph.
//
// EMOJI-IN-UI EXCEPTION: this tree (src/experiments/kids-mode/**)
// is the explicit opt-in to emojis.

import { useEffect, useMemo, useRef, useState } from "react";
import { COLOR, FONT, RADIUS } from "../../lib/tokens";
import { RECIPES } from "../../data/recipes";
import { supabase } from "../../lib/supabase";
import { useKidRecipes } from "../../lib/useKidRecipes";

export function KidMealsManager({ userId, onClose }) {
  const { rows: ownRows, mark, unmark, updateMeta, loading } = useKidRecipes(userId, { ownOnly: true });
  const [userRecipes, setUserRecipes] = useState([]);
  const [query, setQuery] = useState("");

  // Pull the calling adult's own user_recipes once. We only need
  // slug + title + emoji to populate the list — no realtime needed
  // (recipe authoring is rare relative to this screen's lifespan).
  useEffect(() => {
    if (!userId) return;
    let cancelled = false;
    (async () => {
      const { data, error } = await supabase
        .from("user_recipes")
        .select("id, slug, recipe")
        .eq("user_id", userId)
        .order("updated_at", { ascending: false });
      if (cancelled) return;
      if (error) {
        console.warn("[kid-meals] user_recipes:", error.message);
        return;
      }
      const list = (data || []).map((row) => ({
        slug:  row.slug || row.id,
        title: row.recipe?.title || row.slug || "Untitled",
        emoji: row.recipe?.emoji || null,
        source: "user",
      }));
      setUserRecipes(list);
    })();
    return () => { cancelled = true; };
  }, [userId]);

  const markedSlugs = useMemo(() => new Set(ownRows.map((r) => r.recipe_slug)), [ownRows]);
  // Quick lookup from slug → { kid_label, kid_kind } so the marked
  // rows can show the parent's existing nickname + kind without
  // re-querying.
  const metaBySlug = useMemo(() => {
    const m = new Map();
    for (const r of ownRows) {
      m.set(r.recipe_slug, { kid_label: r.kid_label || "", kid_kind: r.kid_kind || "meal" });
    }
    return m;
  }, [ownRows]);

  // Combined list: bundled first (stable), then user recipes.
  const all = useMemo(() => {
    const bundled = RECIPES.map((r) => ({
      slug:  r.slug,
      title: r.title,
      emoji: r.emoji,
      source: "bundled",
    }));
    return [...bundled, ...userRecipes];
  }, [userRecipes]);

  const filtered = useMemo(() => {
    if (!query.trim()) return all;
    const q = query.trim().toLowerCase();
    return all.filter((r) => r.title.toLowerCase().includes(q));
  }, [all, query]);

  // Marked first, then unmarked, each group preserving order.
  const sorted = useMemo(() => {
    const marked = filtered.filter((r) => markedSlugs.has(r.slug));
    const unmark = filtered.filter((r) => !markedSlugs.has(r.slug));
    return [...marked, ...unmark];
  }, [filtered, markedSlugs]);

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(30,30,30,0.45)",
        display: "flex",
        alignItems: "flex-end",
        justifyContent: "center",
        zIndex: 220,
        padding: 16,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "100%",
          maxWidth: 520,
          maxHeight: "85vh",
          background: "#FAF6EE",
          borderRadius: RADIUS.sheet,
          padding: "20px 0 calc(16px + env(safe-area-inset-bottom))",
          boxShadow: "0 -8px 24px rgba(0,0,0,0.18)",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <div style={{ padding: "0 20px 12px", flexShrink: 0 }}>
          <div
            style={{
              fontFamily: FONT.itemTitle,
              fontStyle: "italic",
              fontSize: 22,
              color: COLOR.ink,
              marginBottom: 4,
            }}
          >
            Kid Meals
          </div>
          <div
            style={{
              fontFamily: FONT.forma,
              fontSize: 13,
              color: COLOR.muted,
              marginBottom: 12,
              lineHeight: 1.5,
            }}
          >
            Tap any recipe to add it to what kids can request.
            They'll see it in their Foods → Meals.
          </div>
          <SearchField value={query} onChange={setQuery} />
        </div>

        {/* Scroll area */}
        <div
          style={{
            flex: 1,
            overflowY: "auto",
            WebkitOverflowScrolling: "touch",
            padding: "4px 20px 20px",
          }}
        >
          {loading && sorted.length === 0 ? (
            <div style={{ padding: "20px 0", textAlign: "center", color: COLOR.muted, fontFamily: FONT.forma, fontSize: 13 }}>
              Loading…
            </div>
          ) : sorted.length === 0 ? (
            <div style={{ padding: "20px 0", textAlign: "center", color: COLOR.muted, fontFamily: FONT.forma, fontSize: 13 }}>
              No recipes match.
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {sorted.map((r) => (
                <RecipeRow
                  key={`${r.source}:${r.slug}`}
                  recipe={r}
                  marked={markedSlugs.has(r.slug)}
                  meta={metaBySlug.get(r.slug)}
                  onToggle={async () => {
                    try {
                      if (markedSlugs.has(r.slug)) await unmark(r.slug);
                      else await mark({ slug: r.slug, title: r.title, emoji: r.emoji });
                    } catch (e) {
                      console.error("[kid-meals] toggle:", e?.message || e);
                    }
                  }}
                  onMetaChange={async (patch) => {
                    try {
                      await updateMeta({ slug: r.slug, ...patch });
                    } catch (e) {
                      console.error("[kid-meals] updateMeta:", e?.message || e);
                    }
                  }}
                />
              ))}
            </div>
          )}
        </div>

        <div style={{ padding: "12px 20px 0", flexShrink: 0 }}>
          <button
            type="button"
            onClick={onClose}
            style={{
              width: "100%",
              padding: 14,
              background: COLOR.leaf,
              color: "#fff",
              border: "none",
              borderRadius: 10,
              fontFamily: FONT.mono,
              fontSize: 12,
              fontWeight: 600,
              letterSpacing: "0.08em",
              cursor: "pointer",
            }}
          >
            DONE
          </button>
        </div>
      </div>
    </div>
  );
}

function SearchField({ value, onChange }) {
  const ref = useRef(null);
  // (scroll-into-view on focus is handled globally by the single
  // root focusin listener in useKeyboardOffset.)
  return (
    <input
      ref={ref}
      type="text"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder="Search recipes…"
      style={{
        width: "100%",
        padding: "12px 14px",
        fontFamily: "inherit",
        fontSize: 16,
        background: "#fff",
        border: `1px solid ${COLOR.border}`,
        borderRadius: 10,
        color: COLOR.ink,
        boxSizing: "border-box",
      }}
    />
  );
}

const KIND_OPTIONS = [
  { key: "meal",    label: "Meal",    emoji: "🍽️" },
  { key: "snack",   label: "Snack",   emoji: "🥨" },
  { key: "dessert", label: "Treat",   emoji: "🍰" },
];

function RecipeRow({ recipe, marked, meta, onToggle, onMetaChange }) {
  // Local state for the nickname input so typing feels instant.
  // Debounced into onMetaChange so we don't fire a DB write per
  // keystroke. The echo-guard ref ignores the inbound row update
  // that mirrors our own write so the kid's cursor doesn't jump.
  const [localLabel, setLocalLabel] = useState(meta?.kid_label || "");
  const lastWroteRef = useRef(meta?.kid_label || "");
  useEffect(() => {
    if (typeof meta?.kid_label !== "string") return;
    if (meta.kid_label === lastWroteRef.current) return;
    setLocalLabel(meta.kid_label);
  }, [meta?.kid_label]);

  useEffect(() => {
    if (!marked) return undefined;
    if (localLabel === (meta?.kid_label || "")) return undefined;
    const id = setTimeout(() => {
      lastWroteRef.current = localLabel;
      onMetaChange?.({ kidLabel: localLabel });
    }, 380);
    return () => clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [localLabel, marked]);

  const kind = meta?.kid_kind || "meal";

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: marked ? 8 : 0,
        padding: marked ? "12px 14px" : "0",
        background: marked ? COLOR.leafDeep : "transparent",
        border: marked ? `1px solid ${COLOR.leafDim}` : "none",
        borderRadius: 10,
        transition: "background 120ms ease, border-color 120ms ease",
      }}
    >
      <button
        type="button"
        onClick={onToggle}
        style={{
          all: "unset",
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          gap: 12,
          padding: marked ? 0 : "12px 14px",
          background: marked ? "transparent" : "#fff",
          border: marked ? "none" : `1px solid ${COLOR.border}`,
          borderRadius: marked ? 0 : 10,
          touchAction: "manipulation",
        }}
      >
        <span style={{ fontSize: 26, lineHeight: 1 }}>{recipe.emoji || "🍽️"}</span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              fontFamily: FONT.forma,
              fontSize: 15,
              fontWeight: 600,
              color: COLOR.ink,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {recipe.title}
          </div>
          <div style={{ fontFamily: FONT.mono, fontSize: 10, color: COLOR.sub, letterSpacing: "0.06em" }}>
            {recipe.source === "user" ? "YOUR RECIPE" : "LIBRARY"}
          </div>
        </div>
        <div
          style={{
            width: 28,
            height: 28,
            borderRadius: 8,
            background: marked ? COLOR.leaf : "transparent",
            border: `2px solid ${marked ? COLOR.leaf : COLOR.border}`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "#fff",
            fontSize: 16,
            fontWeight: 700,
          }}
        >
          {marked ? "✓" : ""}
        </div>
      </button>

      {/* Expanded controls — visible only when marked. Lets the
          parent give the kid-facing nickname (e.g. "Egg Burgers")
          and choose which Foods-tab segment the recipe lands in. */}
      {marked && (
        <>
          <input
            type="text"
            value={localLabel}
            onChange={(e) => setLocalLabel(e.target.value)}
            placeholder={`What kids call it (default: ${recipe.title})`}
            maxLength={48}
            style={{
              width: "100%",
              padding: "10px 12px",
              fontFamily: "inherit",
              fontSize: 14,
              background: "#fff",
              border: `1px solid ${COLOR.border}`,
              borderRadius: 8,
              color: COLOR.ink,
              boxSizing: "border-box",
            }}
          />
          <div style={{ display: "flex", gap: 6 }}>
            {KIND_OPTIONS.map((k) => {
              const active = kind === k.key;
              return (
                <button
                  key={k.key}
                  type="button"
                  onClick={() => onMetaChange?.({ kidKind: k.key })}
                  className="mcm-focusable"
                  style={{
                    flex: 1,
                    padding: "8px 4px",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: 2,
                    background: active ? COLOR.leaf : "#fff",
                    border: `1.5px solid ${active ? COLOR.leaf : COLOR.border}`,
                    borderRadius: 8,
                    cursor: "pointer",
                    touchAction: "manipulation",
                  }}
                >
                  <span style={{ fontSize: 18, lineHeight: 1 }}>{k.emoji}</span>
                  <span
                    style={{
                      fontFamily: FONT.mono,
                      fontSize: 10,
                      fontWeight: 700,
                      letterSpacing: "0.06em",
                      color: active ? "#fff" : COLOR.ink,
                    }}
                  >
                    {k.label.toUpperCase()}
                  </span>
                </button>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}

export default KidMealsManager;
